import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';

interface BusOpcion {
  id?: string;
  padron: string;
  placa?: string;
  marca?: string;
  modelo?: string;
  dueno?: string;
  numero?: string;
}

interface BusSelectProps {
  buses: any[];
  value: string;                 // padron elegido ('' = ninguno)
  onChange: (padron: string) => void;
  className?: string;
  placeholder?: string;
}

// Normaliza para comparar: minusculas, sin tildes ni guiones/espacios (AUQ-919 = auq919).
const norm = (s: any) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

// Buscador de bus por teclado: filtra mientras se escribe por padron (parcial), placa o dueno.
// Reemplaza a los <select> de buses, donde con 170 buses solo se podia bajar con el mouse.
export function filtrarBuses<T extends Record<string, any>>(buses: T[], texto: string): T[] {
  const q = norm(texto);
  if (!q) return buses;
  // Solo numeros = busqueda de padron (parcial). Con letras se busca tambien placa, dueno y modelo.
  const soloPadron = /^[0-9]+$/.test(q);
  const puntaje = (b: T) => {
    const p = norm(b.padron);
    if (p === q) return 0;
    if (p.startsWith(q)) return 1;
    if (p.includes(q)) return 2;
    if (soloPadron) return 99;
    if (norm(b.placa).includes(q)) return 3;
    if (norm(b.dueno).includes(q)) return 4;
    if (norm(`${b.marca} ${b.modelo}`).includes(q)) return 5;
    return 99;
  };
  return buses
    .map((b) => ({ b, s: puntaje(b) }))
    .filter((x) => x.s < 99)
    .sort((a, b) => a.s - b.s || norm(a.b.padron).localeCompare(norm(b.b.padron), undefined, { numeric: true }))
    .map((x) => x.b);
}

const BusSelect = ({ buses, value, onChange, className = '', placeholder = 'Escribe el padrón, placa o dueño…' }: BusSelectProps) => {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const caja = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);

  const elegido = buses.find((b) => String(b.padron) === String(value));
  const etiqueta = (b: BusOpcion) => `${b.padron || b.numero} - ${[b.placa, b.marca, b.modelo].filter(Boolean).join(' ')}`;
  const filtrados = useMemo(() => filtrarBuses(buses, texto).slice(0, 60), [buses, texto]);

  useEffect(() => {
    const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  useEffect(() => { setActivo(0); }, [texto]);
  useEffect(() => {
    const el = lista.current?.children[activo] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activo]);

  const elegir = (b: BusOpcion) => { onChange(String(b.padron)); setTexto(''); setAbierto(false); };

  return (
    <div ref={caja} className="relative">
      <div className={`flex items-center border rounded-lg bg-white ${className.includes('mb-') ? className.match(/mb-\d+/)?.[0] : ''}`}>
        <Search className="w-4 h-4 text-gray-400 ml-2 shrink-0" />
        <input
          type="text"
          inputMode="search"
          autoComplete="off"
          value={abierto ? texto : (elegido ? etiqueta(elegido) : texto)}
          placeholder={placeholder}
          onFocus={() => { setAbierto(true); setTexto(''); }}
          onClick={() => { if (!abierto) { setAbierto(true); setTexto(''); } }}
          onChange={(e) => {
            // Si la lista estaba cerrada, la casilla mostraba el bus elegido: se empieza una busqueda
            // nueva solo con lo que se acaba de escribir, no pegado al nombre anterior.
            let v = e.target.value;
            if (!abierto && elegido && v.startsWith(etiqueta(elegido))) v = v.slice(etiqueta(elegido).length);
            setTexto(v); setAbierto(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActivo((i) => Math.min(i + 1, filtrados.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((i) => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') { if (abierto && filtrados[activo]) { e.preventDefault(); elegir(filtrados[activo]); } }
            else if (e.key === 'Escape') { setAbierto(false); }
          }}
          className="w-full p-2 rounded-lg outline-none text-sm"
        />
        {value && (
          <button type="button" title="Quitar bus" onClick={() => { onChange(''); setTexto(''); }} className="px-2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {abierto && (
        <ul ref={lista} className="absolute z-[100] mt-1 w-full max-h-64 overflow-auto bg-white border rounded-lg shadow-lg text-sm">
          {filtrados.length === 0 ? (
            <li className="px-3 py-2 text-gray-400">Ningún bus coincide con “{texto}”</li>
          ) : filtrados.map((b, i) => (
            <li
              key={b.id || b.padron}
              onMouseDown={(e) => { e.preventDefault(); elegir(b); }}
              onMouseEnter={() => setActivo(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === activo ? 'bg-blue-50' : ''} ${String(b.padron) === String(value) ? 'font-semibold' : ''}`}
            >
              <span className="font-mono font-semibold">{b.padron || b.numero}</span>
              <span className="text-gray-500"> · {[b.placa, b.marca, b.modelo].filter(Boolean).join(' ')}</span>
              {b.dueno && <span className="block text-xs text-gray-500">{b.dueno}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BusSelect;
